import React, { useState } from 'react';
import { MoreVertical, Flag, Ban, Trash2, ShieldCheck } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { blockUser, unblockUser } from '../../api/blocks';
import { deleteConversation } from '../../api/messages';
import { useToast } from '../../hooks/use-toast';
import ReportUserDialog from '../ReportUserDialog';

/**
 * ChatHeaderMenu — Phase F4 ⋯ menu: SEGNALA · BLOCCA/SBLOCCA · ELIMINA.
 * Reuses the EXISTING block system and the (minimally extended) reports
 * system. Elimina ≠ Blocca: hide-for-me only, thread returns on new message.
 */
const ChatHeaderMenu = ({ username, iBlockedThem, onBlockChanged, onDeleted, t }) => {
  const { toast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const doBlockToggle = async () => {
    setBusy(true);
    try {
      if (iBlockedThem) {
        await unblockUser(username);
        toast({ title: t('blocked.unblock_success') });
      } else {
        await blockUser(username);
        toast({ title: t('blocked.block_success') });
      }
      onBlockChanged?.();
    } catch (_e) {
      toast({ title: t('blocked.action_failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await deleteConversation(username);
      toast({ title: t('delete_conv.success') });
      setDeleteOpen(false);
      onDeleted?.();
    } catch (_e) {
      toast({ title: t('blocked.action_failed'), variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="rounded-md p-1.5 hover:bg-secondary"
            aria-label={t('menu.aria')}
            data-testid="chat-menu-btn"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="chat-menu-report">
            <Flag className="mr-2 h-4 w-4" /> {t('menu.report')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={doBlockToggle} data-testid={iBlockedThem ? 'chat-menu-unblock' : 'chat-menu-block'}>
            {iBlockedThem
              ? (<><ShieldCheck className="mr-2 h-4 w-4" /> {t('menu.unblock')}</>)
              : (<><Ban className="mr-2 h-4 w-4" /> {t('menu.block')}</>)}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDeleteOpen(true)}
            className="text-red-500 focus:text-red-500"
            data-testid="chat-menu-delete"
          >
            <Trash2 className="mr-2 h-4 w-4" /> {t('menu.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* SEGNALA — Phase F9.3 shared dialog (same reasons/statuses) */}
      <ReportUserDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        username={username}
        testIdPrefix="chat-report"
      />

      {/* ELIMINA ≠ BLOCCA — explicit copy in the confirm dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="chat-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('delete_conv.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('delete_conv.desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="chat-delete-cancel">{t('delete_conv.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="chat-delete-confirm"
            >
              {t('delete_conv.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ChatHeaderMenu;
